import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Building2, Check, ChevronDown } from 'lucide-react';
import { listCompanies } from '../../services/CompaniesService';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useToast } from '../../hooks/useToast';
import type { RootState } from '../../redux/store';
import { readSavedCompanyId } from '../../redux/activeCompanyStorage';
import type { Society } from '../../types/Society.types';
import {
  companiesFailed,
  companiesLoaded,
  companiesLoading,
  selectActiveCompany,
  setActiveCompany
} from '../../redux/slices/activeCompanySlice';

export const CompanySelector = () => {
  const dispatch = useDispatch();
  const { notify } = useToast();
  const { companies, status } = useSelector((state: RootState) => state.activeCompany);
  const activeCompany = useSelector(selectActiveCompany);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (status !== 'idle') return;
    dispatch(companiesLoading());
    listCompanies()
      .then(list => {dispatch(companiesLoaded({ companies: list, preferredId: readSavedCompanyId() }))})
      .catch(err => {
        console.error('Error fetching companies:', err);
        dispatch(companiesFailed());
      });
  }, [status, dispatch]);

  useClickOutside(
    target => Boolean(containerRef.current && containerRef.current.contains(target)),
    () => setIsOpen(false),
    isOpen
  );

  const isLoading = status === 'idle' || status === 'loading';
  const canOpen = status === 'ready' && companies.length > 1;
  const label = isLoading ? 'Cargando…' : activeCompany?.name || 'Sin sociedades';

  const handleSelect = (company: Society) => {
    setIsOpen(false);
    if (company.id === activeCompany?.id) return;
    dispatch(setActiveCompany(company.id));
    notify('Sociedad Cambiada', `Ahora trabajas en ${company.name}.`, 'info');
  };

  return (
    <div className="company-selector" ref={containerRef} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className={`company-selector-trigger ${isOpen ? 'open' : ''}`}
        onClick={() => canOpen && setIsOpen(!isOpen)}
        disabled={!canOpen}
        title={activeCompany ? `Sociedad activa: ${activeCompany.name}` : undefined}
      >
        <Building2 size={14} className="company-selector-icon" />
        <span className="company-selector-name">{label}</span>
        {canOpen && <ChevronDown size={14} className="company-selector-chevron" />}
      </button>

      <div className={`company-selector-menu ${isOpen ? 'open' : ''}`}>
        {companies.map(company => {
          const isActive = company.id === activeCompany?.id;
          return (
            <button
              key={company.id}
              type="button"
              className={`company-selector-item ${isActive ? 'active' : ''}`}
              onClick={() => handleSelect(company)}
            >
              <span className="company-selector-item-text">
                <span className="company-selector-item-name">{company.name}</span>
                {company.cif && <span className="company-selector-item-cif">{company.cif}</span>}
              </span>
              {isActive && <Check size={12} className="company-selector-check" />}
            </button>
          );
        })}
      </div>
    </div>
  );
};
